import { Link } from '@tanstack/react-router'
import { Bone, BookOpen, Check, CircleAlert, EllipsisVertical, ImageOff, Loader2, RotateCcw, Sparkles } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { STATUS_LABEL, type PuppetStatus } from '@/api/puppets'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/components/ui/utils'

/**
 * One puppet tile: picture, name, one status chip, one clear next step. A fixed slot model (same
 * picture box, same two text lines, same button row) so a grid of 800 reads as one rhythm.
 */

const STATUS_TONE: Record<PuppetStatus, BadgeTone> = {
  ready: 'ok',
  needs_moves: 'warn',
  rigging: 'info',
  failed: 'danger',
  none: 'neutral',
}

export function StatusChip({ status, className }: { status: PuppetStatus; className?: string }) {
  if (status === 'none') return null
  return (
    <Badge tone={STATUS_TONE[status]} className={cn('shadow-card', className)} data-testid="puppet-status">
      {status === 'ready' ? <Check className="size-3.5" aria-hidden /> : null}
      {status === 'rigging' ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : null}
      {status === 'failed' ? <CircleAlert className="size-3.5" aria-hidden /> : null}
      {STATUS_LABEL[status]}
    </Badge>
  )
}

/** Picture with a same-size placeholder when there is none (or it fails): no layout shift. */
export function PuppetThumb({ src, name, className }: { src: string | null | undefined; name: string; className?: string }) {
  const [broken, setBroken] = useState(false)
  return (
    <div className={cn('relative flex aspect-square items-center justify-center overflow-hidden bg-paper-2', className)}>
      {src && !broken ? (
        <img src={src} alt="" loading="lazy" decoding="async" draggable={false} onError={() => setBroken(true)} className="size-full object-contain p-2" />
      ) : (
        <span className="flex flex-col items-center gap-1 text-xs text-ink-3">
          <ImageOff className="size-6" aria-hidden />
          <span className="sr-only">No picture of {name}</span>
        </span>
      )}
    </div>
  )
}

export interface PuppetCardProps {
  name: string
  /** Second line (category, "Made from a parts sheet"…). */
  sub?: string | null
  thumb: string | null | undefined
  status: PuppetStatus
  /** Live progress copy while it learns to move. */
  progress?: { msg: string | null; pct: number | null } | null
  error?: string | null
  /** The puppet project, once it exists. */
  projectId?: string | null
  onMakeItMove?: () => void
  /** Extra controls in the top-right corner (a ⋯ menu). */
  corner?: ReactNode
  /**
   * A catalog character's uid: without `corner`, the card gets a ⋯ menu with "Fix its bones…"
   * (`/skel/split?asset=<uid>`: split its own pictures again, check every move, then save or replace).
   */
  assetUid?: string | null
  testId?: string
}

/** The catalog card's ⋯ menu: "Fix its bones…" opens Split to bones on the character's own pictures. */
export function CatalogPuppetMenu({ name, assetUid }: { name: string; assetUid: string }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-9 bg-paper/80 backdrop-blur" aria-label={`More for ${name}`} data-testid="puppet-card-menu">
          <EllipsisVertical aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to="/skel/split" search={{ asset: assetUid }} data-testid="fix-bones">
            <Bone className="size-4" aria-hidden />
            Fix its bones…
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function PuppetCard({ name, sub, thumb, status, progress, error, projectId, onMakeItMove, corner, assetUid, testId = 'puppet-card' }: PuppetCardProps) {
  const pct = progress?.pct != null ? Math.round(Math.min(1, Math.max(0, progress.pct)) * 100) : null
  const cornerNode = corner ?? (assetUid && status !== 'rigging' ? <CatalogPuppetMenu name={name} assetUid={assetUid} /> : null)
  return (
    <li className="card flex min-w-0 flex-col overflow-hidden [content-visibility:auto] [contain-intrinsic-size:auto_320px]" data-testid={testId} data-status={status}>
      <div className="relative">
        <PuppetThumb src={thumb} name={name} />
        <StatusChip status={status} className="absolute left-2 top-2" />
        {cornerNode ? <div className="absolute right-1.5 top-1.5">{cornerNode}</div> : null}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-ink" title={name}>
            {name}
          </h3>
          <p className="min-h-4 truncate text-xs text-ink-3" title={sub ?? undefined}>
            {sub ?? ''}
          </p>
        </div>

        <div className="mt-auto flex flex-col gap-1.5">
          {status === 'none' ? (
            <Button variant="primary" size="md" className="w-full" onClick={onMakeItMove} disabled={!onMakeItMove} aria-label={`Make ${name} move`}>
              <Sparkles aria-hidden />
              Make it move
            </Button>
          ) : status === 'rigging' ? (
            <>
              <Button variant="secondary" size="md" className="w-full" disabled aria-label={`${name} is learning to move`}>
                <Loader2 className="animate-spin" aria-hidden />
                Learning…
              </Button>
              <div className="h-1.5 overflow-hidden rounded-full bg-paper-3" aria-hidden>
                <div className={cn('h-full rounded-full bg-info transition-[width]', pct == null && 'w-1/3 animate-pulse-soft')} style={pct != null ? { width: `${Math.max(6, pct)}%` } : undefined} />
              </div>
              <p className="truncate text-xs text-ink-2" role="status" title={progress?.msg ?? undefined}>
                {progress?.msg ?? 'Learning to move…'}
              </p>
            </>
          ) : status === 'failed' ? (
            <>
              <Button variant="secondary" size="md" className="w-full" onClick={onMakeItMove} disabled={!onMakeItMove} aria-label={`Try again to make ${name} move`}>
                <RotateCcw aria-hidden />
                Try again
              </Button>
              <p className="line-clamp-2 text-xs text-danger" title={error ?? undefined}>
                {error ?? 'It could not be made to move.'}
              </p>
            </>
          ) : projectId ? (
            <>
              <Button asChild variant="secondary" size="md" className="w-full">
                <Link to="/skel/$id" params={{ id: projectId }} aria-label={`Open ${name}`}>
                  Open
                </Link>
              </Button>
              {status === 'ready' ? (
                <Button asChild variant="ghost" size="sm" className="w-full text-accent">
                  <Link to="/stories/new" search={{ cast: `skel:${projectId}` }} aria-label={`Use ${name} in a story`}>
                    <BookOpen aria-hidden />
                    Use in a story
                  </Link>
                </Button>
              ) : (
                <p className="text-center text-xs text-ink-2">Open it to add the moves it needs.</p>
              )}
            </>
          ) : (
            <Button variant="secondary" size="md" className="w-full" disabled>
              <Loader2 className="animate-spin" aria-hidden />
              Finishing…
            </Button>
          )}
        </div>
      </div>
    </li>
  )
}
